/**
 * Data layer for `cc-subscribe`. Thin typed fetch wrappers over /api/v1/cc-subscribe.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type CcSubscribePlanId = "starter" | "growth" | "pro" | "enterprise";
export type CcSubscribeStatus =
	| "pending"
	| "active"
	| "past_due"
	| "cancelled";

export interface CcSubscribeSubscription {
	id: string;
	tenant_id: string;
	plan: CcSubscribePlanId;
	currency: string;
	price_minor: number;
	cycle_days: number;
	status: CcSubscribeStatus;
	payment_intent_id: string | null;
	started_at: number | null;
	next_bill_at: number | null;
	cancelled_at: number | null;
	created_at: number;
	updated_at: number;
	formatted_amount?: string;
}

export interface CcSubscribePlanCatalogEntry {
	code: CcSubscribePlanId;
	currency: string;
	amount_minor: number;
	cycle_days: number;
}

export interface GetMeResponse {
	subscription: CcSubscribeSubscription | null;
	tenant_id: string;
	catalog?: Record<string, CcSubscribePlanCatalogEntry>;
}

export interface CreateIntentBody {
	tenant_id: string;
	plan_code: CcSubscribePlanId;
	currency?: string;
}

export interface CreateIntentResponse {
	payment_intent_id: string;
	status: string;
	plan: CcSubscribePlanId;
	amount_minor: number;
	currency: string;
	formatted_amount: string;
	tenant_id: string;
	created_at: number;
}

export interface ConfirmIntentBody {
	intent_id: string;
	card_token_mock: string;
}

const BASE = "/api/v1/cc-subscribe";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getMe(): Promise<GetMeResponse> {
	const res = await fetch(`${BASE}/subscriptions/me`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<GetMeResponse>(res);
}

export async function createIntent(
	body: CreateIntentBody,
): Promise<CreateIntentResponse> {
	const res = await fetch(`${BASE}/intent`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateIntentResponse>(res);
}

export async function confirmIntent(
	body: ConfirmIntentBody,
): Promise<CcSubscribeSubscription> {
	const res = await fetch(`${BASE}/confirm`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CcSubscribeSubscription>(res);
}

export async function cancelSubscription(
	id: string,
): Promise<CcSubscribeSubscription> {
	const res = await fetch(`${BASE}/subscriptions/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ status: "cancelled" }),
	});
	return parseOrThrow<CcSubscribeSubscription>(res);
}

export const ccSubscribeQueryKeys = {
	all: ["cc-subscribe"] as const,
	list: () => [...ccSubscribeQueryKeys.all] as const,
	detail: (id: string) => [...ccSubscribeQueryKeys.all, id] as const,
};
